// Echo-leak detection helpers.
//
// We compare a short window of mic PCM against the corresponding window of the
// playback audio.  If the speaker is leaking into the mic, the two signals will
// be highly correlated at some lag in [0, 300ms] (typical air + Bluetooth
// latency).  Pure shadowing — even when the learner mimics the audio — has low
// time-aligned correlation because the human reaction delay is variable and the
// voice timbre differs from the source.

export function downsamplePcm(input: Float32Array, ratio: number): Float32Array {
  if (ratio <= 1) return input;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    // Simple decimation — anti-alias filtering omitted because the correlation
    // is robust to mild aliasing and the cost matters at 2 Hz tick rate.
    out[i] = input[i * ratio];
  }
  return out;
}

export interface CrossCorrelationOptions {
  // Sample rate of the input arrays (Hz).
  sourceSampleRate: number;
  // Downsample target (Hz). Lower = faster.
  targetSampleRate?: number;
  // Max lag to search, in milliseconds. Default 300ms covers BT latency.
  maxLagMs?: number;
  // Energy floor below which we treat windows as silence and return 0.
  energyFloor?: number;
}

/**
 * Returns the peak normalized cross-correlation in [0, 1] between `mic` (the
 * later signal) and `play` (the source), searching for `mic` being delayed by
 * 0..maxLagMs samples relative to `play`.
 */
export function peakCrossCorrelation(
  mic: Float32Array,
  play: Float32Array,
  opts: CrossCorrelationOptions,
): number {
  const target = opts.targetSampleRate ?? 4000;
  const ratio = Math.max(1, Math.round(opts.sourceSampleRate / target));
  const sr = opts.sourceSampleRate / ratio;
  const maxLagMs = opts.maxLagMs ?? 300;
  const energyFloor = opts.energyFloor ?? 0.01;

  const m = downsamplePcm(mic, ratio);
  const p = downsamplePcm(play, ratio);
  const len = Math.min(m.length, p.length);
  if (len < Math.floor(sr * 0.2)) return 0; // need at least 200ms

  const maxLag = Math.min(len - 1, Math.floor((maxLagMs / 1000) * sr));
  const step = Math.max(1, Math.floor(sr / 1000)); // ~1ms steps
  const windowLen = len - maxLag;

  // Pre-compute play window norm (constant across lags)
  let normP = 0;
  for (let i = 0; i < windowLen; i++) normP += p[i] * p[i];
  if (normP < energyFloor) return 0;

  let peak = 0;
  for (let lag = 0; lag <= maxLag; lag += step) {
    let dot = 0;
    let normM = 0;
    for (let i = 0; i < windowLen; i++) {
      const mv = m[i + lag];
      const pv = p[i];
      dot += mv * pv;
      normM += mv * mv;
    }
    if (normM < energyFloor) continue;
    const corr = Math.abs(dot) / Math.sqrt(normM * normP);
    if (corr > peak) peak = corr;
  }
  return peak;
}
